import React, { useRef } from "react"
import { Menu as MenuIcon, X } from "react-feather"
import tw from "twin.macro"
import { animated, useTransition } from "react-spring"
import { Link, useStaticQuery, graphql } from "gatsby"
import { plural, addIrregularRule } from "pluralize"
import styled from "@emotion/styled"
import Image from "gatsby-image"

import {
  useToggle,
  useHandleOutsideClick,
  springs,
  irregularPluralWords,
} from "../helpers"

interface Props {
  className?: string
  size?: number
}

export const HamburgerMenu = ({ className, size = 24 }: Props) => {
  const data = useStaticQuery(graphql`
    query HamburgerMenu {
      logo: file(absolutePath: { regex: "/logo-basket-green.png/" }) {
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      categories: allMarkdownRemark {
        group(field: frontmatter___categories) {
          name: fieldValue
        }
      }
    }
  `)

  const categories = data.categories.group

  const [open, toggleOpen] = useToggle()
  const wrapperRef = useRef(null)

  const transitions = useTransition(open, null, {
    from: {
      transform: "translateX(-100%)",
    },
    enter: {
      transform: "translateX(0)",
    },
    leave: {
      transform: "translateX(-100%)",
    },
    config: springs.swift,
  })

  useHandleOutsideClick(wrapperRef, () => toggleOpen(false))

  return (
    <>
      <Styles.Wrapper>
        {open ? (
          <X size={size} className={className} onClick={() => toggleOpen()} />
        ) : (
          <MenuIcon
            size={size}
            className={className}
            onClick={() => toggleOpen()}
          />
        )}
        {transitions.map(
          ({ item, key, props }) =>
            item && (
              <Menu.Wrapper key={key} style={props} ref={wrapperRef}>
                <Link to="/">
                  <Menu.Logo
                    fluid={data.logo.childImageSharp.fluid}
                    alt="MixCart Logo"
                  />
                </Link>
                <Menu.Title>Categories</Menu.Title>
                {categories.map(({ name }: { name: string }) => {
                  if (name in irregularPluralWords) {
                    addIrregularRule(name, name)
                  }

                  return (
                    <Menu.Item key={name}>
                      <Link to={`/categories/${name}`}>{plural(name)}</Link>
                    </Menu.Item>
                  )
                })}
              </Menu.Wrapper>
            )
        )}
      </Styles.Wrapper>
      {open && <Styles.Background />}
    </>
  )
}

const Styles = {
  Wrapper: tw.span`relative`,
  Background: tw.span`before:content before:absolute before:inset-0 before:bg-black before:w-full before:z-40 before:opacity-25`,
}

const Menu = {
  Wrapper: tw(animated.ul)`fixed px-4 py-8 top-0 left-0 h-screen bg-white z-50`,
  Logo: tw(Image)`w-16 mx-auto mb-6`,
  Title: tw.h1`mb-2 text-lg font-semibold tracking-wide uppercase`,
  Item: styled.li`
    ${tw`ml-2 text-sm tracking-wide uppercase`}

    &:not(:last-child) {
      ${tw`mb-2`}
    }
  `,
}
